import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { FaChevronDown, FaPowerOff, FaSave } from "react-icons/fa";
import PageHeader from "src/components/pageHeader";
import PageLoading from "src/components/pageLoading";
import { useTheme } from "src/context/theme.context";
import { auth, db } from "src/firebase";
import { Organization } from "src/features/organizations/types/organization.entity";
import { showToast } from "src/utils/toast.util";

type CoachSignupAttributionConfig = {
  type: "COACH_SIGNUP_ATTRIBUTION";
  enabled: boolean;
  organizationId?: string | null;
};

const CONFIG_COLLECTION = "app_config";
const CONFIG_DOCUMENT_ID = "coachSignupAttribution";
const PRODUCTION_DEFAULT_ORGANIZATION_ID = "70g5EDGGI0Hkw5l0U2MF";
const getDefaultOrganizationId = () =>
  process.env.NODE_ENV === "production" ? PRODUCTION_DEFAULT_ORGANIZATION_ID : "";

const CoachSignupAttributionPage = () => {
  const { isDarkMode } = useTheme();
  const [enabled, setEnabled] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [savedOrganizationId, setSavedOrganizationId] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPageData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const configRef = doc(db, CONFIG_COLLECTION, CONFIG_DOCUMENT_ID);
        const [organizationsSnapshot, configSnapshot] = await Promise.all([
          getDocs(collection(db, "organizations")),
          getDoc(configRef),
        ]);

        const nextOrganizations = organizationsSnapshot.docs.map(
          (organizationDoc) =>
            ({
              id: organizationDoc.id,
              ...organizationDoc.data(),
            } as Organization)
        );

        setOrganizations(
          nextOrganizations.sort((firstOrganization, secondOrganization) =>
            (firstOrganization.name || firstOrganization.id).localeCompare(
              secondOrganization.name || secondOrganization.id
            )
          )
        );

        if (configSnapshot.exists()) {
          const config = configSnapshot.data() as Partial<CoachSignupAttributionConfig>;
          const nextEnabled = Boolean(config.enabled);
          const nextOrganizationId =
            typeof config.organizationId === "string" ? config.organizationId : getDefaultOrganizationId();

          setEnabled(nextEnabled);
          setSavedEnabled(nextEnabled);
          setOrganizationId(nextOrganizationId);
          setSavedOrganizationId(nextOrganizationId);
        } else {
          setEnabled(false);
          setSavedEnabled(false);
          const defaultOrganizationId = getDefaultOrganizationId();
          setOrganizationId(defaultOrganizationId);
          setSavedOrganizationId(defaultOrganizationId);
        }
      } catch (err) {
        console.error("Failed to load coach signup attribution config", err);
        setError("Failed to load coach signup attribution campaign settings.");
      } finally {
        setIsLoading(false);
      }
    };

    loadPageData();
  }, []);

  const selectedOrganization = useMemo(
    () => organizations.find((organization) => organization.id === organizationId),
    [organizationId, organizations]
  );
  const hasProductionDefaultSelected = organizationId === PRODUCTION_DEFAULT_ORGANIZATION_ID;
  const hasOrganizationChanged = savedEnabled && organizationId !== savedOrganizationId;
  const nextButtonEnablesCampaign = !savedEnabled || hasOrganizationChanged;
  const actionButtonLabel = nextButtonEnablesCampaign ? "Save campaign" : "Cancel";

  const handleSave = async (nextEnabled: boolean) => {
    if (nextEnabled && !organizationId) {
      showToast("error", "Select an organization before saving the attribution campaign.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const currentAdminUid = auth.currentUser?.uid;

      if (!currentAdminUid) {
        throw new Error("Missing current admin user.");
      }

      const configRef = doc(db, CONFIG_COLLECTION, CONFIG_DOCUMENT_ID);
      await setDoc(
        configRef,
        {
          type: "COACH_SIGNUP_ATTRIBUTION",
          enabled: nextEnabled,
          organizationId: nextEnabled ? organizationId : null,
          updatedBy: currentAdminUid,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setEnabled(nextEnabled);
      setSavedEnabled(nextEnabled);
      setSavedOrganizationId(nextEnabled ? organizationId : "");
      showToast(
        "success",
        nextEnabled ? "Coach signup attribution campaign saved." : "Coach signup attribution campaign cancelled."
      );
    } catch (err) {
      console.error("Failed to save coach signup attribution config", err);
      const message = "Failed to save coach signup attribution campaign.";
      setError(message);
      showToast("error", message);
    } finally {
      setIsSaving(false);
    }
  };

  const cardClass = isDarkMode
    ? "bg-gray-900 border-gray-700 text-gray-100 shadow-xl shadow-black/20"
    : "bg-white border-gray-200 text-gray-900 shadow-lg shadow-gray-200/70";
  const fieldClass = isDarkMode ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200";
  const mutedClass = isDarkMode ? "text-gray-400" : "text-gray-600";
  const labelClass = isDarkMode ? "text-gray-200" : "text-gray-700";
  const inputClass = isDarkMode
    ? "bg-gray-900 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500"
    : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500";

  if (isLoading) {
    return (
      <div className="page_container">
        <PageHeader text="Coach Signup Attribution" />
        <PageLoading />
      </div>
    );
  }

  return (
    <div className="page_container">
      <PageHeader text="Coach Signup Attribution" />

      <div className={`max-w-2xl mx-auto rounded-lg border overflow-hidden ${cardClass}`}>
        <div className={`border-b px-6 py-5 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-lg font-semibold">Campaign settings</p>
              <p className={`mt-1 text-sm leading-6 ${mutedClass}`}>
                When enabled, new coach and dual-user signups will be attributed to the selected organization.
              </p>
            </div>
            <span
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                enabled
                  ? isDarkMode
                    ? "bg-emerald-500/15 text-emerald-300"
                    : "bg-emerald-50 text-emerald-700"
                  : isDarkMode
                  ? "bg-gray-800 text-gray-300"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              <FaPowerOff className="h-3 w-3" />
              {enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
        </div>

        <div className="p-6">

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={(event) => event.preventDefault()} className="space-y-6">
          <div className={`rounded-lg border p-4 ${fieldClass}`}>
            <label htmlFor="coach-signup-attribution-organization" className={`block text-sm font-medium mb-2 ${labelClass}`}>
              Organization <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="coach-signup-attribution-organization"
                value={organizationId}
                onChange={(event) => setOrganizationId(event.target.value)}
                disabled={isSaving || organizations.length === 0}
                className={`w-full appearance-none rounded-lg border px-3 py-3 pr-10 text-sm focus:outline-none focus:ring-2 ${inputClass} ${
                  isSaving || organizations.length === 0 ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <option value="">Select organization</option>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name || organization.id}
                  </option>
                ))}
              </select>
              <FaChevronDown className={`pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${mutedClass}`} />
            </div>
            <p className={`mt-2 text-xs ${mutedClass}`}>
              {organizations.length === 0
                ? "No organizations were found in Firestore."
                : selectedOrganization
                ? `Selected: ${selectedOrganization.name || selectedOrganization.id}${
                    hasProductionDefaultSelected && process.env.NODE_ENV === "production"
                      ? " (production default)"
                      : ""
                  }`
                : "Select the organization that should receive new coach signup attribution."}
            </p>
          </div>

          <div className={`flex justify-end border-t pt-5 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
            <button
              type="button"
              onClick={() => handleSave(nextButtonEnablesCampaign)}
              disabled={isSaving}
              className={`inline-flex min-w-[150px] items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition-colors ${
                isSaving
                  ? "bg-blue-400 cursor-not-allowed"
                  : nextButtonEnablesCampaign
                  ? "bg-blue-600 hover:bg-blue-700 shadow-sm"
                  : "bg-red-600 hover:bg-red-700 shadow-sm"
              }`}
            >
              {nextButtonEnablesCampaign ? <FaSave className="w-4 h-4" /> : <FaPowerOff className="w-4 h-4" />}
              {isSaving ? "Saving..." : actionButtonLabel}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default CoachSignupAttributionPage;
